import { CheckIcon } from "@radix-ui/react-icons";
import { Button } from "./button";
import { Card, CardContent, CardHeader, CardTitle } from "./card";
import EnhancedButton from "./enhanced-button";
import { AnimatePresence, motion } from "motion/react";
import { Tooltip, TooltipTrigger, TooltipContent } from "./tooltip";
import { cn, TOPUP_CONFIG } from "@/lib/utils";
import type { User } from "firebase/auth";
import type { BillingData } from "@/types/auth";
import { PlanType } from "@/types/auth";
import { createCheckoutSession, getBillingPortalUrl } from "@/lib/api";
import { toast } from "sonner";
import { FillAppError } from "@/lib/FillAppError";
import { Skeleton } from "@/components/ui/skeleton";
import posthog from "posthog-js";

interface PlanFeature {
  text: string;
  hasTooltip?: boolean;
  tooltipText?: string;
}

interface PricingCardProps {
  id: string;
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  monthlyCredits?: number;
  isYearly: boolean;
  emoji: string;
  isPopular?: boolean;
  buttonText: string;
  buttonVariant?: "default" | "outline";
  features: PlanFeature[];
  includes?: string;
  disclaimer?: string;
  disclaimerTooltip?: string;
  useCaseLabel?: string;
  href?: string;
  // Dynamic props for auth/billing-aware behavior
  user?: User | null;
  billingData?: BillingData | null;
  isLoadingComplete?: boolean;
  lookupKey?: string;
}

export default function PricingCard({
  id,
  name,
  monthlyPrice,
  yearlyPrice,
  monthlyCredits,
  isYearly,
  emoji,
  isPopular = false,
  buttonText,
  buttonVariant = "default",
  features,
  includes,
  disclaimer,
  disclaimerTooltip,
  useCaseLabel,
  href = "/signup?redirect=/dashboard",
  user = null,
  billingData = null,
  isLoadingComplete = false,
  lookupKey,
}: PricingCardProps) {
  const isGradientButton = buttonVariant === "default" && isPopular;
  
  // Calculate discount percentage for yearly plans
  const discountPercentage = monthlyPrice > 0 
    ? Math.round(((monthlyPrice * 12 - yearlyPrice) / (monthlyPrice * 12)) * 100)
    : 0;
  const isFree = monthlyPrice === 0 && yearlyPrice === 0;

  const isAuthenticated = Boolean(user);
  const userPlanType = billingData?.planType;
  const isUserOnFree = userPlanType === PlanType.FREE;
  const isUserOnStarter = userPlanType === PlanType.STARTER_MONTHLY || userPlanType === PlanType.STARTER_YEARLY;
  const isUserOnPro = userPlanType === PlanType.PRO_MONTHLY || userPlanType === PlanType.PRO_YEARLY;

  const isPaygCard = id === 'payg';
  const isStarterCard = id === 'starter';
  const isProCard = id === 'pro';

  // Determine visibility and state of the primary action button
  let shouldRenderButton = true;
  let resolvedButtonText = buttonText;
  let resolvedVariant: "default" | "outline" = buttonVariant;
  let resolvedDisabled = false;
  let onClickHandler: (() => Promise<void>) | undefined;
  let resolvedHref: string | undefined = href;

  // Pay-as-you-go remains available to every account.
  if (!isAuthenticated) {
    // Keep default behavior for unauthenticated users
    shouldRenderButton = true;
    resolvedHref = isPaygCard ? `/signup?redirect=${encodeURIComponent('/dashboard/billing#buy-credits')}` : href;
    onClickHandler = undefined;
  } else {
    // Authenticated users: compute per current plan and card
    if (isPaygCard) {
      resolvedButtonText = 'Buy credits';
      resolvedHref = '/dashboard/billing#buy-credits';
      onClickHandler = undefined;
    } else if (isUserOnFree) {
      if (isPaygCard) {
        // Free user on the pay-as-you-go card: link to billing to buy credits
        resolvedButtonText = 'Buy credits';
        resolvedVariant = 'default';
        resolvedHref = '/dashboard/billing';
        onClickHandler = undefined;
      } else if (isStarterCard || isProCard) {
        // Subscribe via checkout to the selected plan
        resolvedButtonText = buttonText;
        resolvedVariant = isStarterCard && isPopular ? 'default' : buttonVariant;
        resolvedHref = undefined;
        onClickHandler = async () => {
          try {
            posthog.capture("subscription_creation_attempted", {
              planId: id,
              planName: name,
              billingPeriod: isYearly ? "yearly" : "monthly",
              price: isYearly ? yearlyPrice : monthlyPrice,
              lookupKey,
            });
            if (!lookupKey) {
              throw new Error('Missing plan lookup key');
            }
            const checkoutUrl = await createCheckoutSession(lookupKey);
            window.location.href = checkoutUrl;
          } catch (error) {
            posthog.capture("subscription_creation_failed", {
              planId: id,
              planName: name,
              billingPeriod: isYearly ? "yearly" : "monthly",
              price: isYearly ? yearlyPrice : monthlyPrice,
              lookupKey,
              error: error instanceof Error ? error.message : String(error),
            });
            if (error instanceof FillAppError) {
              toast.error(error.message);
            } else {
              toast.error('Something went wrong. Please try again.');
            }
          }
        };
      }
    } else if (isUserOnStarter) {
      if (isPaygCard) {
        // Hide free/payg plan button when on Starter
        shouldRenderButton = false;
      } else if (isStarterCard) {
        // Current plan
        resolvedButtonText = 'Current plan';
        resolvedVariant = 'outline';
        resolvedDisabled = true;
        resolvedHref = undefined;
        onClickHandler = undefined;
      } else if (isProCard) {
        // Upgrade to Pro via billing portal
        resolvedButtonText = 'Upgrade to Pro';
        resolvedVariant = 'default';
        resolvedHref = undefined;
        onClickHandler = async () => {
          try {
            posthog.capture("billing_portal_upgrade_attempted", {
              fromPlan: "starter",
              toPlan: "pro",
            });
            const portalUrl = await getBillingPortalUrl();
            window.location.href = portalUrl;
          } catch (error) {
            if (error instanceof FillAppError) {
              toast.error(error.message);
            } else {
              toast.error('Something went wrong. Please try again.');
            }
          }
        };
      }
    } else if (isUserOnPro) {
      if (isPaygCard || isStarterCard) {
        // Hide lower-tier plan buttons when on Pro
        shouldRenderButton = false;
      } else if (isProCard) {
        // Current plan
        resolvedButtonText = 'Current plan';
        resolvedVariant = 'outline';
        resolvedDisabled = true;
        resolvedHref = undefined;
        onClickHandler = undefined;
      }
    }
  }

  return (
    <>
    <Card className={cn("border bg-card text-card-foreground relative flex flex-1 flex-col justify-between p-0 shadow-none rounded-3xl", 
      isPopular && "lg:-my-10 lg:py-10",
      !isPopular && "lg:border-none lg:mt-[1px]",
    )}>

      <div className="relative h-full">
        <div className="flex flex-col gap-6">
          <CardHeader className="flex flex-col space-y-1.5 p-6">
            <CardTitle className="text-lg leading-none tracking-tight flex flex-row items-center justify-start gap-2 font-medium">
              <span className="text-xl">{emoji}</span>
              <p className="font-semibold text-lg">{name}</p>
              {isPopular && (
                <span className="font-light inline-flex items-center gap-1 px-2.5 py-1 bg-foreground text-background text-xs rounded-full scale-90 origin-left">
                  {isPaygCard ? "No subscription" : "Most Popular"}
                </span>
              )}
            </CardTitle>
          </CardHeader>

          <CardContent className="flex flex-col gap-6 p-0">
            <div className="flex w-full flex-col items-start justify-between gap-4 px-6 lg:h-[150px]">
                              <div className="mb-6 flex flex-col gap-2">
                  <div className="flex flex-row items-center gap-2 overflow-hidden">
                    <span className="flex font-normal text-4xl tabular-nums leading-none tracking-[-0.15rem]">
                      $
                      {isFree ? (
                        <span className="inline-block">{isPaygCard ? TOPUP_CONFIG.PRICE_PER_CREDIT.toFixed(2) : 0}</span>
                      ) : (
                        <div className="overflow-hidden inline-block">
                          <AnimatePresence mode="wait">
                            <motion.span
                              key={`${isYearly}-${monthlyPrice}-${yearlyPrice}`}
                              initial={{ opacity: 0, y: -20 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 20 }}
                              transition={{ duration: 0.16, ease: "easeInOut" }}
                              className="inline-block"
                            >
                              {isYearly
                                ? (yearlyPrice / 12) % 1 === 0 
                                  ? (yearlyPrice / 12).toString()
                                  : (yearlyPrice / 12).toFixed(2)
                                : monthlyPrice.toString()}
                            </motion.span>
                          </AnimatePresence>
                        </div>
                      )}
                    </span>
                    
                    {/* Discount Badge */}
                    <AnimatePresence>
                      {!isFree && isYearly && discountPercentage > 0 && (
                        <motion.span
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ 
                            duration: 0.2, 
                            ease: "easeOut",
                            delay: 0.1
                          }}
                          className={"inline-flex items-center px-2 py-1 bg-gradient-primary-rotated text-xs font-semibold rounded-full ml-2 text-white bg-primary"}
                        >
                          {discountPercentage}% off
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </div>
                  <div className="line-clamp-1 font-medium text-muted-foreground text-xs tabular-nums">
                    {isFree ? (
                      <span className="inline-block">{isPaygCard ? `per credit · From $${TOPUP_CONFIG.MIN_USD_AMOUNT}` : "per month"}</span>
                    ) : (
                      <AnimatePresence mode="wait">
                        <motion.span
                          key={`period-${isYearly}-${yearlyPrice}`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{
                            duration: 0.16,
                            ease: "easeInOut",
                            delay: 0.1
                          }}
                          className="inline-block"
                        >
                          {isYearly
                            ? `per month, $${yearlyPrice} billed annually`
                            : "per month"}
                        </motion.span>
                      </AnimatePresence>
                    )}
                  </div>
                  {useCaseLabel && (
                    <p className="text-muted-foreground text-xs mt-1">{useCaseLabel}</p>
                  )}
                </div>

              {!isLoadingComplete ? (
                <div className="w-full">
                  <Skeleton className="h-12 w-full rounded-lg" />
                </div>
              ) : (
                shouldRenderButton && (
                <div className="fade-in animate-in transition-opacity duration-300 w-full">
                  {isGradientButton && !onClickHandler && resolvedHref ? (
                    <EnhancedButton 
                      href={resolvedHref}
                      fullWidth
                      onClick={() => {
                        posthog.capture("plan_signup_clicked", {
                          planId: id,
                          planName: name,
                          billingPeriod: isYearly ? "yearly" : "monthly",
                          price: isYearly ? yearlyPrice : monthlyPrice,
                        });
                      }}
                    >
                      {resolvedButtonText}
                    </EnhancedButton>
                  ) : isGradientButton && onClickHandler ? (
                    <EnhancedButton onClick={onClickHandler} fullWidth>
                      {resolvedButtonText}
                    </EnhancedButton>
                  ) : (
                    <>
                      {resolvedHref ? (
                        <a className="contents" href={resolvedHref}>
                          <Button
                            variant={resolvedVariant}
                            className="px-5 py-3 h-12 rounded-lg w-full"
                            disabled={resolvedDisabled}
                            onClick={() => {
                              posthog.capture("plan_signup_clicked", {
                                planId: id,
                                planName: name,
                                billingPeriod: isYearly ? "yearly" : "monthly",
                                price: isYearly ? yearlyPrice : monthlyPrice,
                              });
                            }}
                          >
                            {resolvedButtonText}
                          </Button>
                        </a>
                      ) : (
                        <Button
                          variant={resolvedVariant}
                          className="px-5 py-3 h-12 rounded-lg w-full"
                          disabled={resolvedDisabled}
                          onClick={onClickHandler}
                        >
                          {resolvedButtonText}
                        </Button>
                      )}
                    </>
                  )}
                </div>
                )
              )}
            </div>

            <div className="shrink-0 h-px w-full mt-2 bg-secondary"></div>

            <ul className="mt-4 flex flex-col gap-4 px-6 pb-8 font-medium text-sm">
              {includes && (
                <span className="flex items-center gap-1 font-medium text-sm">
                  {includes}
                </span>
              )}
                              {features.map((feature, index) => (
                  <li
                    key={index}
                    className="flex flex-row items-start gap-3 font-normal text-sm"
                  >
                    {index === 0 && monthlyCredits !== undefined ? (
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <span className="text-3xl font-medium tabular-nums tracking-tight">
                          {monthlyCredits.toLocaleString('en-US')}
                        </span>
                        <span className="text-sm font-medium">AI credits/month</span>
                      </div>
                    ) : feature.hasTooltip && feature.tooltipText ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-pointer underline decoration-1 decoration-foreground decoration-dotted underline-offset-1 flex flex-row items-start gap-3">
                            <CheckIcon className="size-5 shrink-0" />
                            <span>{feature.text}</span>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>{feature.tooltipText}</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <>
                        <CheckIcon className="size-5 shrink-0" />
                        <span>{feature.text}</span>
                      </>
                    )}
                  </li>
                ))}
              {disclaimer && (
                <p className="text-muted-foreground text-xs">
                  {disclaimerTooltip ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0} className="cursor-help underline decoration-dotted underline-offset-2">
                          {disclaimer}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>{disclaimerTooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : disclaimer}
                </p>
              )}
            </ul>
          </CardContent>
        </div>
        
      </div>
    </Card>

    {/* {!isLast && (
      <div className="hidden w-px self-stretch bg-border lg:block"></div>
    )} */}
    </>
  );
}
